// Resolves the post-login redirect target (`?next=` param / iframe
// loggedInRedirect). Absolute URLs are only honored for allowlisted origins —
// this allowlist is the open-redirect defense, so no wildcards and no
// pattern-matching. Everything else absolute is dropped; relative paths stay
// in-app via history.push.

export const EXTERNAL_REDIRECT_ALLOWLIST: ReadonlyArray<string> = [
  "https://developer.allstar.gg",
];

// Auth flow pages are never a destination worth returning to.
const AUTH_PATH =
  /^\/(register|login|signup|invite|welcome|oauth|authenticate)(\/|\?|$)/;
export const isAuthPath = (path: string): boolean => AUTH_PATH.test(path);

// Old CTAs send ?redirect=, embeds send ?loggedInRedirect=; all mean next.
export const getNextParam = (params: URLSearchParams): string | null =>
  params.get("next") ??
  params.get("redirect") ??
  params.get("loggedInRedirect");

// Adds next=<current page> so login or signup brings the user back here.
export const withReturn = (path: string): string => {
  const { pathname, search } = window.location;
  if (isAuthPath(pathname)) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}next=${encodeURIComponent(pathname + search)}`;
};

// Saved return paths expire so an abandoned flow can't redirect a later login.
// Values saved without a timestamp (older writers) are still honored.
const NEXT_TTL_MS = 60 * 60 * 1000;

export const storeNext = (path: string): void => {
  localStorage.setItem("next", path);
  localStorage.setItem("nextAt", String(Date.now()));
};

export const readNext = (): string | null => {
  const savedAt = Number(localStorage.getItem("nextAt"));
  if (savedAt && Date.now() - savedAt > NEXT_TTL_MS) {
    localStorage.removeItem("next");
    localStorage.removeItem("nextAt");
    return null;
  }
  return localStorage.getItem("next");
};

export type TLoginRedirect =
  | { kind: "external"; url: string }
  | { kind: "internal"; path: string }
  | null;

/**
 * Resolves and performs the post-login navigation. Returns true if it
 * navigated; false when the target was missing or dropped by the allowlist,
 * so callers can apply their own fallback (or none).
 */
export const followLoginRedirect = (
  target: string | null | undefined,
  push: (path: string) => void,
): boolean => {
  const resolved = resolveLoginRedirect(target);
  if (!resolved) return false;

  if (resolved.kind === "external") {
    window.location.assign(resolved.url);
  } else {
    push(resolved.path);
  }
  return true;
};

export const resolveLoginRedirect = (
  target: string | null | undefined,
): TLoginRedirect => {
  if (!target) return null;

  let parsed: URL | null = null;
  try {
    parsed = new URL(target);
  } catch {
    parsed = null;
  }

  if (parsed) {
    // Covers javascript:, data:, http-downgrade, and foreign origins — only an
    // exact allowlisted origin survives.
    if (!EXTERNAL_REDIRECT_ALLOWLIST.includes(parsed.origin)) return null;
    return { kind: "external", url: parsed.toString() };
  }

  // Protocol-relative URLs ("//evil.example") don't parse as absolute but
  // would still leave the site — drop them.
  if (target.startsWith("//")) return null;

  return { kind: "internal", path: target };
};
